import { ReactElement } from "react"

import { InstallerApplicationStatuses } from "../../../constants/enums"
import { UNKNOWN_STATUS } from "../../../constants/generalConstants"
import { arrayOnCondition } from "../../../utils/general/generalUtils"
import AppStepper from "../../dataDisplay/stepper/appStepper"

const APPLICATION_CREATED = "Application Created"
const CUT_OUT_SCANNED = "Cut-out Image Scanned"
const ENA_ASSESSED = "Application assessed by Connect Direct"
const APPLICATION_APPROVED = "Application Approved"
const CX_SUBMITTED = "Commissioning Information Submitted"
const CX_APPROVED = "Commissioning Information Approved"
const CX_SENT_BACK = "Commissioning Sent Back"

const AUTO_APPROVED_STEP_LABELS = (dno: string, isGen: boolean): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Approved by Connect Direct",
    `${dno ? `${dno} ` : ""}notified of outcome`,
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_APPROVED_STEP_LABELS = (dno: string, isGen: boolean): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Application successful, approved by DNO",
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_APPROVED_WITH_LIMIT_STEP_LABELS = (
    dno: string,
    isGen: boolean
): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    `Approved with limit by ${dno ? `${dno} ` : ""}`,
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const DNO_FURTHER_INFO_REQUIRED_STEP_LABELS = (dno: string): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    "Reviewed by DNO & further information required",
]

const AWAITING_ASSESSMENT_STEP_LABELS = (isGen: boolean): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    "Awaiting Connect Direct assessment",
    ENA_ASSESSED,
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const UNDER_ASSESSMENT_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    "Under assessment by Connect Direct",
    ENA_ASSESSED,
]

const IMAGE_SCANNING_STEP_LABELS = (isGen: boolean): string[] => [
    APPLICATION_CREATED,
    "Cut-Out Image Being Scanned",
    "Awaiting Connect Direct assessment",
    ENA_ASSESSED,
    ...arrayOnCondition(isGen, [CX_SUBMITTED, CX_APPROVED]),
]

const ESCALATED_STEP_LABELS = (dno: string): string[] => [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    `Escalated ${dno ? `to ${dno} ` : ""}by Connect Direct`,
    `Decision made by ${dno}`,
]

const FAILED_TO_LARGE_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Application unsuccessful - no further action will be taken",
]

const IMAGE_SCAN_FAILED_STEPS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    "Image scan failed - no further action will be taken",
]

const INVALID_CUT_OUT_STEPS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    "Cut-out image invalid - no further action will be taken",
    "Please resubmit a valid cut-out image",
]

const FAILED_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Application unsuccessful - no further action will be taken",
]

const FALLBACK_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    ENA_ASSESSED,
    "Error retrieving status",
]

const CX_SUBMITTED_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_APPROVED,
]

const CX_SENT_BACK_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_SENT_BACK,
    CX_APPROVED,
]

const CX_APPROVED_STEP_LABELS = [
    APPLICATION_CREATED,
    CUT_OUT_SCANNED,
    APPLICATION_APPROVED,
    CX_SUBMITTED,
    CX_APPROVED,
]

type TStepper = {
    steps: string[]
    currentStep: number
}

interface IStepperRecognisedDispatcher {
    [key: string]: TStepper
}

const STEPPER_DISPATCHER = (
    dno: string,
    isGeneration: boolean
): IStepperRecognisedDispatcher => ({
    [InstallerApplicationStatuses.autoApproved]: {
        steps: AUTO_APPROVED_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.dnoApproved]: {
        steps: DNO_APPROVED_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.dnoApprovedWithLimit]: {
        steps: DNO_APPROVED_WITH_LIMIT_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.dnoFurtherInfoRequired]: {
        steps: DNO_FURTHER_INFO_REQUIRED_STEP_LABELS(dno),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.awaitingAssessment]: {
        steps: AWAITING_ASSESSMENT_STEP_LABELS(isGeneration),
        currentStep: 3,
    },
    [InstallerApplicationStatuses.underAssessment]: {
        steps: UNDER_ASSESSMENT_STEP_LABELS,
        currentStep: 3,
    },
    [InstallerApplicationStatuses.imageScanning]: {
        steps: IMAGE_SCANNING_STEP_LABELS(isGeneration),
        currentStep: 2,
    },
    [InstallerApplicationStatuses.escalatedToDno]: {
        steps: ESCALATED_STEP_LABELS(dno),
        currentStep: 4,
    },
    [InstallerApplicationStatuses.failedToLarge]: {
        steps: FAILED_TO_LARGE_STEP_LABELS,
        currentStep: 4,
    },
    [InstallerApplicationStatuses.failed]: {
        steps: FAILED_STEP_LABELS,
        currentStep: 4,
    },
    [InstallerApplicationStatuses.imageScanFailed]: {
        steps: IMAGE_SCAN_FAILED_STEPS,
        currentStep: 3,
    },
    [InstallerApplicationStatuses.invalidCutOut]: {
        steps: INVALID_CUT_OUT_STEPS,
        currentStep: 3,
    },
    [InstallerApplicationStatuses.cxSubmitted]: {
        steps: CX_SUBMITTED_STEP_LABELS,
        currentStep: 4,
    },
    [InstallerApplicationStatuses.cxUpdateRequired]: {
        steps: CX_SENT_BACK_STEP_LABELS,
        currentStep: 4,
    },
    [InstallerApplicationStatuses.cxAccepted]: {
        steps: CX_APPROVED_STEP_LABELS,
        currentStep: 5,
    },
    [InstallerApplicationStatuses.reAutoApproved]: {
        steps: AUTO_APPROVED_STEP_LABELS(dno, isGeneration),
        currentStep: 5,
    },
    [InstallerApplicationStatuses.reEscalatedToDno]: {
        steps: ESCALATED_STEP_LABELS(dno),
        currentStep: 4,
    },
    [UNKNOWN_STATUS]: {
        steps: FALLBACK_STEP_LABELS,
        currentStep: 4,
    },
})

interface InstallerApplicationProgressStepperProps {
    status: InstallerApplicationStatuses | string
    dno: string
    isGeneration: boolean
}

/**
 * Renders a stepper for the installer application progress.
 */
const InstallerApplicationProgressStepper = ({
    status,
    dno,
    isGeneration,
}: InstallerApplicationProgressStepperProps): ReactElement => {
    const stepperConfig =
        STEPPER_DISPATCHER(dno, isGeneration)[status] ||
        STEPPER_DISPATCHER("", isGeneration)[UNKNOWN_STATUS]
    return (
        <AppStepper
            stepLabels={stepperConfig.steps}
            currentStepIndex={stepperConfig.currentStep}
        />
    )
}

export default InstallerApplicationProgressStepper
